import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ImageCarousel, 
  ImageGrid, 
  ProfessionalImage, 
  professionalImages 
} from "./ImageCarousel.js";

// Commercial Solutions - Enterprise Page
export const EnterprisePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Commercial Solutions for Enterprise Businesses</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Client Customized hardware solutions and services tailored to meet the timelines and requirements of the global Enterprise!
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Enterprise Technology Challenges</h2>
              <p className="text-lg text-gray-600 mb-6">
                Large Enterprise Businesses often face equally large obstacles and challenges when finding their place in the digital age. Security threats are on the rise, new platforms and services require moving away from legacy business models.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Data storage rates are growing 40-60% per year to keep up with the growing need for internal and consumer data analytics. So how do large enterprises maintain their market share while keeping their technology up to date with competitors and a changing business climate?
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🔒</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Rising Security Threats</h4>
                    <p className="text-gray-600">Advanced protection against evolving cyber threats</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🔄</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Legacy Modernization</h4>
                    <p className="text-gray-600">Move away from legacy systems to modern platforms</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">📈</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Data Growth Management</h4>
                    <p className="text-gray-600">Handle 40-60% annual data storage growth</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Enterprise Solutions</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Security & Compliance:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Enterprise-grade cybersecurity solutions</li>
                      <li>• Regulatory compliance frameworks</li>
                      <li>• Risk management strategies</li>
                      <li>• Advanced threat detection</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Platform Modernization:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Legacy system migration</li>
                      <li>• Modern platform implementation</li>
                      <li>• Cloud transformation strategies</li>
                      <li>• Digital business model adoption</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Data Analytics & Storage:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Scalable data storage solutions</li>
                      <li>• Advanced analytics platforms</li>
                      <li>• Consumer data insights</li>
                      <li>• Performance optimization</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Competitive Advantage:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Market-leading technology adoption</li>
                      <li>• Innovation strategy development</li>
                      <li>• Digital transformation roadmaps</li>
                      <li>• Change management support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Maintain Market Leadership</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            RTS is here to help enterprise clients with solutions tailored to their business requirements
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Get Enterprise Solutions
          </button>
        </div>
      </section>
    </div>
  );
};

// Commercial Main Page
export const CommercialPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Commercial Solutions</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Reliable Technology and Support When You and Your Business Need Support
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Keep Your Business Running Smoothly</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              For small and mid-sized businesses, unexpected downtimes can have a negative impact on the bottom line. RTS is here to help our clients with solutions tailored to their business requirements!
            </p>
          </div>

          {/* Professional Business Meeting Images */}
          <div className="mb-16">
            <ImageCarousel 
              images={professionalImages.meetings} 
              autoPlay={true} 
              interval={8000}
            />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div 
              className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition duration-300 cursor-pointer"
              onClick={() => navigate('/commercial/small-business')}
            >
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Small Businesses</h3>
              <p className="text-gray-600 mb-6">
                Time and resource saving products, services, and support for reducing small business technology costs.
              </p>
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold mb-4 inline-block">
                Cost-Effective Solutions
              </div>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li>• Affordable IT support packages</li>
                <li>• Scalable technology solutions</li>
                <li>• Preventive maintenance programs</li>
                <li>• Budget-friendly hardware options</li>
              </ul>
              <button className="text-blue-800 font-semibold hover:text-blue-900 transition duration-300">
                Learn More →
              </button>
            </div>

            <div 
              className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition duration-300 cursor-pointer"
              onClick={() => navigate('/commercial/mid-market')}
            >
              <div className="text-4xl mb-4">🏬</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Mid-Market Businesses</h3>
              <p className="text-gray-600 mb-6">
                Top of the line and cost-efficient solutions for assisting Mid-Market Businesses in maintaining growth.
              </p>
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold mb-4 inline-block">
                Growth-Oriented
              </div>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li>• Scalable infrastructure solutions</li>
                <li>• Compliance management</li>
                <li>• Zero-downtime strategies</li>
                <li>• 24/7 monitoring and support</li>
              </ul>
              <button className="text-blue-800 font-semibold hover:text-blue-900 transition duration-300">
                Learn More →
              </button>
            </div>

            <div 
              className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition duration-300 cursor-pointer"
              onClick={() => navigate('/commercial/enterprise')}
            >
              <div className="text-4xl mb-4">🏭</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Enterprise Businesses</h3>
              <p className="text-gray-600 mb-6">
                Client customized hardware solutions and services tailored to meet the timelines and requirements of global enterprises.
              </p>
              <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold mb-4 inline-block">
                Enterprise-Grade
              </div>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li>• Advanced cybersecurity solutions</li>
                <li>• Legacy system modernization</li>
                <li>• Data analytics platforms</li>
                <li>• Digital transformation</li>
              </ul>
              <button className="text-blue-800 font-semibold hover:text-blue-900 transition duration-300">
                Learn More →
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why RTS Commercial Solutions?</h2>
            <p className="text-xl text-gray-600">Tailored technology solutions for businesses of all sizes</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "💼",
                title: "Business-Focused",
                description: "Solutions designed specifically for business needs"
              },
              {
                icon: "💰",
                title: "Cost-Effective",
                description: "Maximize ROI with efficient technology investments"
              },
              {
                icon: "📈",
                title: "Scalable Growth",
                description: "Technology that grows with your business"
              },
              {
                icon: "🛡️",
                title: "Reliable Support",
                description: "24/7 support to keep your business running"
              }
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Optimize Your Business Technology?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us to discuss how RTS can support your commercial technology needs
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Get Commercial Solutions
          </button>
        </div>
      </section>
    </div>
  );
};

// Partners Page
export const PartnersPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Our Partners and Alliances</h1>
          <p className="text-xl max-w-3xl mx-auto">
            We build strategic partnerships and alliances with the industry's most respected providers to offer our customers best-in-class solutions
          </p>
        </div>
      </section>

      {/* Industry Partners */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">IT Industry Partners</h2>
            <p className="text-xl text-gray-600">Authorized partners with leading technology manufacturers</p>
          </div>

          {/* Professional Technology Partnership Images */}
          <div className="mb-16">
            <ImageGrid 
              images={[
                {
                  url: "https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc1NTY2Mjg0Nnww&ixlib=rb-4.1.0&q=85",
                  alt: "Professional team collaboration meeting",
                  title: "Strategic Partnerships",
                  description: "Building strong industry relationships"
                },
                {
                  url: "https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzU1NjYyODUzfDA&ixlib=rb-4.1.0&q=85",
                  alt: "Business meeting with technology partners",
                  title: "Technology Integration",
                  description: "Collaborative technology solutions"
                },
                {
                  url: "https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg",
                  alt: "Advanced technology infrastructure",
                  title: "Innovation Excellence",
                  description: "Cutting-edge technology partnerships"
                }
              ]}
              columns={3}
            />
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                name: "Cisco",
                category: "Computer Networking",
                description: "Network infrastructure and security solutions",
                certifications: ["Cisco Networking", "Cloud Computing", "WIFI Networking"]
              },
              {
                name: "Dell Technologies",
                category: "PCs/Servers/Storage/Networking/SW",
                description: "Complete enterprise hardware solutions",
                certifications: ["PowerEdge Server Training", "Desktop Training", "Laptop Training"]
              },
              {
                name: "HP/HPE",
                category: "Printing & Scanning / Enterprise",
                description: "Business computing and enterprise solutions",
                certifications: ["ProLiant Server Solutions", "Diagnostic Engineer Training"]
              },
              {
                name: "Microsoft",
                category: "Software & Cloud Services",
                description: "Productivity and cloud platform solutions",
                certifications: ["Azure Certified", "Office 365", "Windows Server"]
              },
              {
                name: "Fortinet",
                category: "Cyber Security Works",
                description: "Proactive cyber security services and solutions",
                certifications: ["Security Appliances", "Network Security", "Threat Protection"]
              },
              {
                name: "Lenovo",
                category: "PCs/Servers/Storage/Services",
                description: "Business computing solutions and support",
                certifications: ["Diagnostic Engineer Training", "Desktop Training", "Laptop Training"]
              },
              {
                name: "APC by Schneider Electric",
                category: "Power Management Solutions",
                description: "Uninterruptible power supply and power management",
                certifications: ["Power Protection", "Data Center Solutions"]
              },
              {
                name: "Ubiquiti Networks",
                category: "Wireless Networks",
                description: "Enterprise wireless networking solutions",
                certifications: ["Wireless Design", "Network Implementation"]
              },
              {
                name: "Zebra Technologies",
                category: "Data Capture & RFID",
                description: "Industrial printing and data capture solutions",
                certifications: ["Barcode Solutions", "RFID Technology"]
              }
            ].map((partner, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold mb-4 inline-block">
                  {partner.category}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{partner.name}</h3>
                <p className="text-gray-600 mb-4">{partner.description}</p>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Certifications:</h4>
                  <ul className="text-sm text-gray-600">
                    {partner.certifications.map((cert, certIndex) => (
                      <li key={certIndex}>• {cert}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Delivery Partners */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Service Delivery Partners</h2>
            <p className="text-xl text-gray-600">Strategic partnerships for comprehensive government contract delivery</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Partnership Approach</h3>
              <p className="text-lg text-gray-600 mb-6">
                These are business partners who are part of our teams when bidding on government contracts. They are our subcontractors under our prime contracts. Conversely, they could be the prime contracts with RTS functioning as their subcontractor.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">🤝</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Integrated Team Approach</h4>
                    <p className="text-gray-600">Collaborative teams for government contract delivery</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">📋</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Project Management</h4>
                    <p className="text-gray-600">Key competency working with internal and subcontractor teams</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-800 mr-3 mt-1">📜</span>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Contract Expertise</h4>
                    <p className="text-gray-600">Recipient of many IDIQ contracts including GSA Schedules</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Partnership Network</h3>
                
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Extensive Network</h4>
                    <p className="text-sm text-blue-700">
                      RTS has partnered with well over 100 different subcontractors to provide customers with responsive, on-time delivery of consulting and technical services.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Service Areas:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Professional Services Technology</li>
                      <li>• Systems Integration</li>
                      <li>• Government Contract Support</li>
                      <li>• Technical Consulting Services</li>
                      <li>• Project Management Services</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Delivery Excellence:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Worry-free service delivery</li>
                      <li>• On-time project completion</li>
                      <li>• Quality assurance processes</li>
                      <li>• Customer satisfaction focus</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IT Industry Certifications */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">IT Industry Certifications</h2>
            <p className="text-xl text-gray-600">Our team maintains industry-leading certifications</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">CompTIA Certifications</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• CompTIA A+ Certified</li>
                <li>• CompTIA Network+ Certified</li>
                <li>• CompTIA Server+ Certified</li>
                <li>• CompTIA Security+ Certified</li>
                <li>• CompTIA Linux+ Certified</li>
                <li>• CompTIA CTT+ Certified</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Manufacturer Training</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Dell PowerEdge Training</li>
                <li>• Dell Desktop/Laptop Training</li>
                <li>• HP ProLiant Server Solutions</li>
                <li>• Lenovo Diagnostic Training</li>
                <li>• Cisco Networking Training</li>
                <li>• Cisco Cloud Computing</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Specialized Skills</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Cross-trained Engineers</li>
                <li>• Hardware/Software Integration</li>
                <li>• Latest IT Technologies</li>
                <li>• Government Compliance</li>
                <li>• Security Clearances</li>
                <li>• Project Management</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Partner with Industry Leaders</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Leverage our strategic partnerships and certified expertise for your next project
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Contact Partnership Team
          </button>
        </div>
      </section>
    </div>
  );
};

// Careers Page (Enhanced)
export const CareersPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Join Our Team</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Build your career with a growing Veteran Owned Small Business that values excellence, integrity, and innovation
          </p>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">What We Offer</h2>
            <p className="text-xl text-gray-600">Our philosophy of employee advancement rests on providing unlimited equal opportunity</p>
          </div>

          {/* Professional Workplace Images */}
          <div className="mb-16">
            <ImageCarousel 
              images={[
                {
                  url: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzU1NjYyOTQ0fDA&ixlib=rb-4.1.0&q=85",
                  alt: "Active workspace with people working in modern office",
                  caption: "Dynamic work environment fostering innovation and growth"
                },
                {
                  url: "https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc1NTY2Mjg0Nnww&ixlib=rb-4.1.0&q=85",
                  alt: "Professional team collaboration and mentorship",
                  caption: "Collaborative culture with mentorship and development opportunities"
                },
                {
                  url: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzU1NjYyOTQ0fDA&ixlib=rb-4.1.0&q=85",
                  alt: "Modern professional boardroom for career development",
                  caption: "Professional meeting spaces supporting career advancement"
                }
              ]}
              autoPlay={true} 
              interval={7000}
            />
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Career Philosophy</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Growth Opportunities</h4>
                  <p className="text-gray-600">People, especially RTS employees, wish to excel and grow, and they will if they are given the opportunity and shown how.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Continuous Learning</h4>
                  <p className="text-gray-600">If you wish to grow, make sure you have tasks in your assignment you have never done before. If you know exactly how to do your job, you may be stagnant.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Success Metrics</h4>
                  <p className="text-gray-600">The ultimate test of a job well done is if our customer gives us another more challenging job.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Team Excellence</h4>
                  <p className="text-gray-600">RTS is bigger than the sum of its parts. Teamwork can bring about values not inherent in individual players.</p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Our Benefits Package</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Core Benefits:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Competitive salaries</li>
                      <li>• Career development programs</li>
                      <li>• Outstanding benefits package</li>
                      <li>• Professional growth opportunities</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Full-Time Employee Benefits:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Health Insurance</li>
                      <li>• Dental & Vision Coverage</li>
                      <li>• Short Term Disability</li>
                      <li>• Long Term Disability</li>
                      <li>• 401(k) Retirement Plan</li>
                      <li>• Dependent & Medical Flexible Spending Accounts</li>
                      <li>• Tuition Reimbursement</li>
                      <li>• Life Insurance</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Work Environment</h4>
                    <p className="text-sm text-blue-700">
                      Safe and smoke-free work environment with challenging jobs and competitive compensation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Hire */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Who We Hire</h2>
            <p className="text-xl text-gray-600">RTS has many challenging and exciting opportunities available. Come be a part of a diverse workforce!</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">🎖️</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Military/Veterans</h3>
              <p className="text-gray-600 mb-4">
                We employ veterans at every level of the organization, from staff to senior leadership. We recognize the talent, character, and commitment veterans bring.
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Security clearance opportunities</li>
                <li>• Military experience valued</li>
                <li>• Leadership development</li>
                <li>• Veteran-friendly culture</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">👨‍💼</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Experienced Professionals</h3>
              <p className="text-gray-600 mb-4">
                Seasoned IT professionals with proven expertise in technology implementation, project management, and client services.
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Advanced technical roles</li>
                <li>• Project management positions</li>
                <li>• Client-facing opportunities</li>
                <li>• Leadership positions</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">🎓</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">College Graduates/Interns</h3>
              <p className="text-gray-600 mb-4">
                Recent graduates and interns looking to start their careers in technology with mentorship and growth opportunities.
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Entry-level positions</li>
                <li>• Mentorship programs</li>
                <li>• Training and development</li>
                <li>• Career progression paths</li>
              </ul>
            </div>
          </div>

          {/* Current Opportunities */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Current Job Openings</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Senior Network Engineer",
                  type: "Full-time",
                  location: "Remote/On-site",
                  security: "Security Clearance Required",
                  description: "Design and implement network infrastructure for government and commercial clients."
                },
                {
                  title: "Cybersecurity Analyst",
                  type: "Full-time", 
                  location: "Remote/On-site",
                  security: "Secret Clearance Preferred",
                  description: "Monitor security systems and respond to cybersecurity incidents."
                },
                {
                  title: "Cloud Solutions Architect",
                  type: "Full-time",
                  location: "Remote/On-site",
                  security: "Public Trust",
                  description: "Design and implement cloud solutions using Microsoft Azure and other platforms."
                },
                {
                  title: "Project Manager",
                  type: "Full-time",
                  location: "Remote/On-site",
                  security: "No Clearance Required",
                  description: "Manage IT implementation projects from conception to completion."
                }
              ].map((job, index) => (
                <div key={index} className="border border-gray-200 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">{job.title}</h4>
                  <p className="text-gray-600 text-sm mb-4">{job.description}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{job.type}</span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">{job.location}</span>
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded">{job.security}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Join Our Team?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Send us your resume and let's discuss how you can contribute to our mission
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Apply Now
          </button>
        </div>
      </section>
    </div>
  );
};